import { QA_API_DOMAIN, fetchWithSupabaseAuth } from "@/api/classifier";

const CODE_RULE_AUDITS_URL = `${QA_API_DOMAIN}/code-rule-audits`;

export type CodeType = "ICD-10" | "OPCS-4.11";
export type ConcernSeverity = "high" | "medium" | "low";

export type CodeRuleAuditRequest = {
  encounterId: string;
  diagnoses: string[];
  procedures: string[];
  fresh?: boolean;
};

export type CodeRuleAuditConcern = {
  relatedCodes: string[];
  codeType: CodeType;
  severity: ConcernSeverity;
  ruleExcerpt: string;
  reasoning: string;
  suggestedAction: string | null;
};

export type CodeRuleAuditResult = {
  summary: string;
  concerns: CodeRuleAuditConcern[];
};

export type CodeRuleAuditResponse = CodeRuleAuditResult & { completedAt?: string };

export async function runCodeRuleAudit(
  { fresh, ...body }: CodeRuleAuditRequest,
  signal?: AbortSignal,
): Promise<CodeRuleAuditResponse> {
  const res = await fetchWithSupabaseAuth(CODE_RULE_AUDITS_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(fresh === true ? { ...body, fresh } : body),
    signal,
  });

  if (res.ok) return res.json() as Promise<CodeRuleAuditResponse>;

  const errorBody = (await res.json().catch(() => ({}))) as { error?: string };
  throw new Error(errorBody.error ?? `Rule check failed (${res.status}).`);
}

export type ScreenLikelihood = "low" | "high" | "saved";

export type CodeRuleScreenRequest = Omit<CodeRuleAuditRequest, "fresh">;

const isScreenLikelihood = (value: unknown): value is ScreenLikelihood =>
  value === "low" || value === "high" || value === "saved";

export async function screenCodeRuleAudit(
  body: CodeRuleScreenRequest,
  signal?: AbortSignal,
): Promise<ScreenLikelihood> {
  try {
    const res = await fetchWithSupabaseAuth(`${CODE_RULE_AUDITS_URL}/screen`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });
    if (!res.ok) return "high";

    const parsed = (await res.json()) as { likelihood?: unknown };
    return isScreenLikelihood(parsed.likelihood) ? parsed.likelihood : "high";
  } catch (err) {
    if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) throw err;
    return "high";
  }
}
